import { keyExtractorId } from "@/src/common/keyExtractors";
import { useLastActiveEmployees, useUser } from "@/src/OS/Login/State";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  Circle,
  EmployeeAvatar,
  ListItem,
  TouchableBox,
  useLayoutContext,
} from "arkh";
import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { FlatList, Modal, Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useCurrentEmployeeSelectControllerState } from "./useCurrentEmployeeSelectControllerState";
import { EmployeeSelectControllerComponent_Store$key } from "./__generated__/EmployeeSelectControllerComponent_Store.graphql";
import { EmployeeSelectControllerQuery } from "./__generated__/EmployeeSelectControllerQuery.graphql";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { AbsolutePopoverHostName } from "@/src/components/mobile/AbsolutePopover";
import { Portal } from "@gorhom/portal";
import { FullWindowOverlay } from "react-native-screens";
import { EmployeeToast } from "./EmployeeToast";

import { FlashList } from "@shopify/flash-list";
import { EmployeeSelectControllerListItem_employee$key } from "./__generated__/EmployeeSelectControllerListItem_employee.graphql";
import { SearchInput } from "../Products/SearchInput";

import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";

const EmployeeSelectListItem = React.memo(EmployeeSelectListItemC);
function EmployeeSelectModalListComponent(props: {
  store: EmployeeSelectControllerComponent_Store$key;
}) {
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const [isSearching, setIsSearching] = useState(false);
  const [query, setQuery] = useState("");
  const store = useFragment(
    graphql`
      fragment EmployeeSelectControllerComponent_Store on Store {
        id
        employees(
          orderBy: AUTO
          autoOrderEmployeeIds: $autoOrderEmployeeIds
          autoOrderTeamId: $autoOrderTeamId
        ) {
          edges {
            node {
              id
              name
              ...Avatar_employee
              ...EmployeeSelectControllerListItem_employee
            }
          }
        }
      }
    `,
    props.store
  );

  const { currentEmployeeId, setCurrentEmployeeId } = usePersistedEmployeeId();

  const employees = useMemo(() => {
    return store.employees?.edges.map((e) => e.node) ?? [];
  }, [store.employees?.edges]);

  const currentEmployee = useMemo(() => {
    return employees.find((e) => e.id === currentEmployeeId);
  }, [employees, currentEmployeeId]);

  const listData = useMemo(() => {
    return employees.filter((e) => {
      if (e.id === currentEmployeeId) {
        return false;
      }
      if (query !== "") {
        return e.name?.toLocaleLowerCase().includes(query.toLocaleLowerCase());
      }
      return true;
    });
  }, [employees, currentEmployeeId, query]);

  const onPress = useCallback(
    (employeeId: string) => {
      setCurrentEmployeeId(employeeId);
    },
    [setCurrentEmployeeId]
  );

  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number] }) => {
      return (
        <EmployeeSelectListItem
          employee={item}
          isCompact={isCompact}
          onPress={onPress}
        />
      );
    },
    [isCompact, onPress]
  );

  const listProps = {
    data: listData,
    renderItem,
    ItemSeparatorComponent: AHSeparator,
    keyExtractor: keyExtractorId,
    keyboardShouldPersistTaps: "handled" as const,
  };
  return (
    <Box style={{ flex: 1 }}>
      <SearchInput
        isSearching
        query={query}
        onChange={(e) => setQuery(e.nativeEvent.text)}
        onFocus={() => setIsSearching(true)}
        onBlur={() => setIsSearching(false)}
        returnKeyType="done"
        autoCapitalize="words"
      />
      {Platform.OS === "web" ? (
        <FlatList {...listProps} />
      ) : (
        <FlashList
          {...listProps}
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
        />
      )}
      {currentEmployee && !isSearching ? (
        <Box
          spaceHorizontal="medium"
          spaceVertical="large"
          cornerRadius="large"
          color="surface-primary"
          shadow="medium"
        >
          <TouchableBox
            onPress={() => setCurrentEmployeeId(currentEmployee.id)}
          >
            <AHText type="detail" color="primary">
              <FormattedMessage
                defaultMessage="Continue as"
                description="Select user primary suggestion title"
              />
            </AHText>
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              <AHText
                type="title"
                style={{ flex: 1 }}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {currentEmployee?.name ?? ""}
              </AHText>
              <EmployeeAvatar employee={currentEmployee} size="large" />
            </Box>
          </TouchableBox>
        </Box>
      ) : null}
    </Box>
  );
}

function EmployeeSelectListItemC(props: {
  employee: EmployeeSelectControllerListItem_employee$key;
  onPress: (employeeId: string) => void;
  isCompact: boolean;
}) {
  const employee = useFragment(
    graphql`
      fragment EmployeeSelectControllerListItem_employee on Employee {
        id
        name
        ...Avatar_employee
      }
    `,
    props.employee
  );
  return (
    <ListItem
      onPress={() => {
        props.onPress(employee.id);
      }}
      right={<EmployeeAvatar employee={employee} size="large" />}
    >
      <AHText
        spaceVertical={props.isCompact ? "none" : "small"}
        type="title-small"
        color="text-secondary"
      >
        {employee.name}
      </AHText>
    </ListItem>
  );
}

function EmployeeSelectModalList(props: { fetchKey: string }) {
  const usr = useUser();
  const lastActive = useLastActiveEmployees();
  const data = useLazyLoadQuery<EmployeeSelectControllerQuery>(
    graphql`
      query EmployeeSelectControllerQuery(
        $autoOrderEmployeeIds: [ID!]
        $autoOrderTeamId: ID
      ) {
        currentStore {
          ...EmployeeSelectControllerComponent_Store
        }
      }
    `,
    {
      autoOrderEmployeeIds: lastActive,
      autoOrderTeamId: usr.currentTeamId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (data.currentStore) {
    return <EmployeeSelectModalListComponent store={data.currentStore} />;
  }
  return null;
}

function EmployeeSelectModalHeader(props: { close: () => void }) {
  return (
    <Box
      spaceHorizontal="medium"
      spaceVertical={Platform.OS === "android" ? "medium" : "large"}
      style={{ flexDirection: "row" }}
    >
      <Box style={{ flex: 1 }}>
        <AHText type="title-small" spaceBottom="xsmall">
          <FormattedMessage
            defaultMessage="Group Account"
            description="Employee select title"
          />
        </AHText>
        <AHText type="subtitle" color="text-secondary">
          <FormattedMessage
            defaultMessage="Select profile to continue"
            description="Select user subtitle"
          />
        </AHText>
      </Box>
      <TouchableBox onPress={() => props.close()}>
        <Circle size="large" color="surface-backgroundControl">
          <AHIcon type="clear" size="large" color="text-primary" />
        </Circle>
      </TouchableBox>
    </Box>
  );
}

function FullScreen(props: { children: React.ReactNode; close: () => void }) {
  if (Platform.OS === "ios") {
    return <FullWindowOverlay>{props.children}</FullWindowOverlay>;
  }
  return (
    <Portal hostName={AbsolutePopoverHostName}>
      <Modal onRequestClose={props.close} visible transparent>
        {props.children}
      </Modal>
    </Portal>
  );
}

function EmployeeSelectModal() {
  const insets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  const layout = useLayoutContext();
  const { dismiss } = useCurrentEmployeeSelectControllerState();
  return (
    <FullScreen
      close={() => {
        dismiss();
      }}
    >
      <Box
        spaceHorizontal="medium"
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.6)",
          alignItems: "center",
          justifyContent: isCompact ? "flex-start" : "center",
          width: "100%",
          height: "100%",
          paddingTop: insets.top + layout.space.large,
          paddingBottom: insets.bottom + layout.space.large,
        }}
        insets={insets}
      >
        <Box
          shadow="medium"
          style={{
            height: "100%",
            width: "100%",
            maxHeight: 800,
            maxWidth: 500,
          }}
          color="surface-primary"
          cornerRadius="large"
        >
          <Box
            color="surface-primary"
            cornerRadius="large"
            style={{ overflow: "hidden", flex: 1 }}
          >
            <EmployeeSelectModalHeader
              close={() => {
                dismiss();
              }}
            />
            <AHSeparator />
            <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
              {(fetchKey) => <EmployeeSelectModalList fetchKey={fetchKey} />}
            </LazyQueryNetworkBoundrary>
          </Box>
        </Box>
      </Box>
    </FullScreen>
  );
}

export function EmployeeSelectController() {
  const { employeeSelectState } = useCurrentEmployeeSelectControllerState();

  switch (employeeSelectState) {
    case "modal":
      return <EmployeeSelectModal />;
    case "toast":
      return <EmployeeToast />;
    default:
      return null;
  }
}
